// ============================================================================
// LÓGICA DE DEMANDA HÍDRICA TEÓRICA Y PROGRAMA DE RIEGO
// ============================================================================
// Portada desde web/calculadora_demanda.html (API ERA5 v2). Funciones PURAS
// (sin dependencias de Vue) para poder testear y reutilizar en el stepper del
// Calendario de Riego.
//
//   Módulo 1 · Demanda teórica:  ETc = Kc·ETo, precip. efectiva, demanda
//              bruta/neta (l/s y m³), volumen anual.
//   Módulo 2 · Programa de riego: TAW = AD·Zr, RAW = TAW·p, tasa de aplicación,
//              frecuencia, dosis, tiempo de riego, horas/año.
//
// Referencias de las fórmulas: modales "Metodología" del HTML original
// (modelo Uso_Agrícola_UCHILE / CASUB, FAO-56, Ley N° 18.450).

// Nombres de mes abreviados. Antes era un array FIJO en español, con lo que un
// proyecto en inglés veía "Ene Feb Mar" en el eje del gráfico de demanda, al
// lado de ejes que Highcharts ya rotulaba en inglés.
//
// Se mantiene como ARRAY (no función) porque hay 8 consumidores, incluidos
// `v-for` en templates y escrituras a filas de datos (`{ mes: MESES[i] }`).
// El contenido lo refresca `refrescarMeses()`, que llama applyProjectLocale()
// al resolver el Formato Regional: así la identidad del array no cambia y
// ningún consumidor necesita enterarse.
import { tiposRiego, texturas, defaultsAgronomicos } from '@/services/agronomia';
export const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/** Nombres de mes abreviados de un locale, derivados de Intl (no hardcodeados). */
export function mesesAbreviados(locale) {
  const fmt = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => {
    const t = fmt.format(new Date(Date.UTC(2021, i, 1))).replace(/\.$/, '');
    return t.charAt(0).toUpperCase() + t.slice(1);
  });
}

/** Reescribe MESES in-place con los nombres del locale indicado. */
export function refrescarMeses(locale) {
  try {
    const nuevos = mesesAbreviados(locale);
    for (let i = 0; i < 12; i += 1) MESES[i] = nuevos[i];
  } catch {
    // Locale inválido: se conservan los nombres que ya estaban.
  }
  return MESES;
}
export const DIAS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Default neutral (sin datos climáticos aún) para eto/pp: 12 ceros, no una
// serie de ejemplo. computeDemanda() con esto da demanda 0, no un resultado
// que aparente ser real.
const ZERO_MONTHS = Array(12).fill(0);

// Kc/Zr/p por cultivo: migrados a BD (especies_cultivo / especies_cultivo_kc,
// ver migrations/2026_07_24_redesign_especies_catalog.sql en agrobyte-api).
// Se consultan vía GET /api/especies-cultivo y GET /api/info-config
// (especieKcFuentes/especieZrDefault/especiePDefault) — ya no hay objeto
// hardcodeado ni inferencia de cultivo por texto acá.

// Eficiencias por tipo de riego, texturas y defaults: ya NO se declaran acá.
// La fuente única es el backend (`helpers/agronomia.js`), servida por
// GET /api/agronomia/constantes y cacheada en `services/agronomia.js`.
//
// Se reexportan los computed del servicio (`tiposRiego.value`), NO arrays
// planos: la carga es asíncrona y un array capturado al importar se quedaría con
// el fallback para siempre.
export { tiposRiego, texturas, defaultsAgronomicos };

// Precipitación efectiva (mm/mes). method: 'usda' | 'pct'.
export function effectivePrecip(p, method = 'usda', pct = 80) {
  if (method === 'pct') return p * (Number(pct) || 0) / 100;
  const pe = p <= 250 ? p * (125 - 0.2 * p) / 125 : 125 + 0.1 * p;
  return Math.max(0, Math.min(pe, p));
}

// ----------------------------------------------------------------------------
// Módulo 1 · Demanda teórica
// ----------------------------------------------------------------------------
// params: { eto[12], pp[12], kc[12], ef(%), ha, unit('ls'|'m3'), peMethod, pePct }
export function computeDemanda(params) {
  const { eto = ZERO_MONTHS, pp = ZERO_MONTHS, kc = KC.olivo.fuentes.uchile.kc, ef: efRaw = 90, ha: haRaw = 0, unit = 'ls', peMethod = 'usda', pePct = 80 } = params || {};
  const ef = Number(efRaw) || 1;
  const ha = Number(haRaw) || 0;

  const rows = [];
  const totals = { etoSum: 0, ppSum: 0, peSum: 0, etcSum: 0, netMmSum: 0, volB: 0, volN: 0, sumB: 0, sumN: 0, kcSum: 0 };

  for (let i = 0; i < 12; i++) {
    const kcM = Number(kc[i]) || 0;
    const etoM = Number(eto[i]) || 0;
    const ppM = Number(pp[i]) || 0;
    const etc = kcM * etoM;
    const pe = effectivePrecip(ppM, peMethod, pePct);
    const netMm = Math.max(etc - pe, 0);
    // 8,64 × días_mes convierte mm·ha/mes a l/s.
    const demB = (etc / (ef / 100)) * ha / (8.64 * DIAS[i]);
    const demN = (netMm / (ef / 100)) * ha / (8.64 * DIAS[i]);
    const vB = (etc / (ef / 100)) * 10 * ha;
    const vN = (netMm / (ef / 100)) * 10 * ha;

    totals.etoSum += etoM; totals.ppSum += ppM; totals.peSum += pe;
    totals.etcSum += etc; totals.netMmSum += netMm;
    totals.volB += vB; totals.volN += vN; totals.sumB += demB; totals.sumN += demN;
    totals.kcSum += kcM;

    rows.push({ mes: MESES[i], kc: kcM, eto: etoM, pp: ppM, etc, pe, netMm, demB, demN, volB: vB, volN: vN });
  }

  const kcAvg = totals.kcSum / 12;
  const avgB = unit === 'ls' ? totals.sumB / 12 : totals.volB / 12;
  const avgN = unit === 'ls' ? totals.sumN / 12 : totals.volN / 12;

  return {
    rows,
    totals: { ...totals, kcAvg },
    unit,
    kpis: {
      bruta: avgB,          // demanda media (l/s) o volumen medio (m³/mes)
      neta: avgN,
      volAnual: totals.volB, // volumen bruto anual (m³/año)
      etcAnual: totals.etcSum // ETc anual (mm/año)
    }
  };
}

// ----------------------------------------------------------------------------
// Módulo 2 · Programa de riego
// ----------------------------------------------------------------------------
// params: { demanda(resultado de computeDemanda), ef(%), ad(mm/m), zr(m), p,
//           estrategia('fija'|'agotamiento'), freqFija, qemisor(l/h), nemit,
//           dp(m), dh(m) }
export function computePrograma(params) {
  const {
    demanda, ef: efRaw = defaultsAgronomicos.value.eficiencia, ad = 0, zr = 0, p = defaultsAgronomicos.value.p,
    estrategia = 'fija', freqFija = 2,
    qemisor = 0, nemit = 0, dp = 1, dh = 1
  } = params || {};
  if (!demanda) return null;

  const ef = Number(efRaw) || 1;
  const areaP = (Number(dp) || 1) * (Number(dh) || 1);           // m² por planta
  const qP = (Number(qemisor) || 0) * (Number(nemit) || 0);      // l/h por planta
  const tasa = areaP > 0 ? qP / areaP : 0;                       // mm/h
  const dens = areaP > 0 ? 10000 / areaP : 0;                    // plantas/ha
  const TAW = (Number(ad) || 0) * (Number(zr) || 0);             // mm
  const RAW = TAW * (Number(p) || 0);                            // mm (dosis neta máx)

  const rows = [];
  let freqPeak = NaN; let timePeak = 0; let nrTot = 0; let hrsTot = 0; let LTot = 0; let peakNet = 0;

  for (let i = 0; i < 12; i++) {
    const net = demanda.rows[i].netMm;
    const netD = net / DIAS[i];
    if (netD <= 0.0001) {
      rows.push({ mes: MESES[i], etc: demanda.rows[i].etc, pe: demanda.rows[i].pe, riego: false, netD: 0, dosisN: 0, dosisB: 0, freq: null, nR: 0, tiempo: 0, horas: 0, Lmes: 0 });
      continue;
    }
    let dosisN; let freq;
    if (estrategia === 'agotamiento') { dosisN = RAW; freq = RAW / netD; }
    else { freq = Number(freqFija) || 1; dosisN = netD * freq; }
    const dosisB = dosisN / (ef / 100);
    const tiempo = tasa > 0 ? dosisB / tasa : 0;    // h por riego
    const nR = DIAS[i] / freq;                       // riegos en el mes
    const volPl = dosisB * areaP;                    // l por planta / riego
    const horas = nR * tiempo;
    const Lmes = nR * volPl;

    nrTot += nR; hrsTot += horas; LTot += Lmes;
    if (netD > peakNet) { peakNet = netD; freqPeak = freq; timePeak = tiempo; }

    rows.push({ mes: MESES[i], etc: demanda.rows[i].etc, pe: demanda.rows[i].pe, riego: true, netD, dosisN, dosisB, freq, nR, tiempo, horas, Lmes });
  }

  return {
    rows,
    meta: { taw: TAW, raw: RAW, tasa, dens },
    kpis: {
      freqPeak: Number.isFinite(freqPeak) ? freqPeak : null, // días (mes peak)
      timePeak,                                              // h (mes peak)
      nrTot,                                                 // riegos/año
      hrsTot                                                 // horas/año
    }
  };
}

// ----------------------------------------------------------------------------
// Módulo 2b · Dimensionamiento MENSUAL del evento de riego
// ----------------------------------------------------------------------------
// La tabla "Programa mensual (detalle)" ya dimensiona cada mes por separado
// (dosis, t/riego, horas): la demanda neta cambia mes a mes, así que con una
// frecuencia constante el mismo programa riega 16 h en agosto y 40 h en
// octubre. El calendario, en cambio, solo tenía UN valor por programa (el del
// mes de mayor exigencia, que es el que se persiste en riegos_programados) y lo
// repetía en todos los eventos.
//
// Esta función traduce las filas mensuales del programa a lo que necesita cada
// evento del calendario —duración, volumen y caudal— para que el evento de un
// mes use SIEMPRE el tiempo de riego de ese mes. Es la misma aritmética del
// evento único (ver computeEventoFija en CalendarioRiegos.vue), aplicada fila a
// fila; no cambia el cálculo de demanda ni de programación.
//
//   volumen (m³)  = dosis bruta (mm) × ha × 10
//   t/riego (h)   = dosis bruta (mm) / tasa (mm/h)        [ya viene en la fila]
//   caudal (l/s)  = volumen × 1000 / (t/riego × 3600)
//
// Nota de validación: al depender ambos de la dosis bruta, volumen y duración
// escalan en la MISMA proporción entre meses, así que el caudal resultante es
// constante para todos los meses (= tasa × ha × 10000/3600), como corresponde a
// una propiedad del sistema de riego y no del mes.
//
// params: programa (resultado de computePrograma), areaHa
// retorna: array de 12 posiciones (índice = mes 0-11); null en los meses sin
//          riego (la lluvia cubre la demanda), null completo si no hay programa.
export function buildProgramaMensual(programa, areaHa = 0) {
  const rows = programa?.rows;
  if (!Array.isArray(rows) || rows.length < 12) return null;
  const ha = Number(areaHa) || 0;
  const r4 = (v) => Math.round(v * 10000) / 10000;
  return rows.map((r, i) => {
    const tiempoH = Number(r?.tiempo) || 0;
    if (!r?.riego || tiempoH <= 0) return null;
    const dosisB = Number(r.dosisB) || 0;
    const volumenM3 = dosisB * ha * 10;
    return {
      mes: i,
      tiempoH: r4(tiempoH),
      volumenM3: r4(volumenM3),
      caudalLs: r4((volumenM3 * 1000) / (tiempoH * 3600)),
      dosisBrutaMm: r4(dosisB)
    };
  });
}

// Fila mensual (buildProgramaMensual) del mes `mesIndex` (0-11). Busca primero
// por posición y valida el campo `mes`; si el array llegara reordenado (JSON de
// un programa guardado), cae a la búsqueda por campo. null cuando ese mes no
// tiene riego o el programa no trae distribución mensual.
export function mesDelPrograma(mensual, mesIndex) {
  if (!Array.isArray(mensual) || !Number.isFinite(Number(mesIndex))) return null;
  const i = Number(mesIndex);
  const porIndice = mensual[i];
  if (porIndice && Number(porIndice.mes ?? i) === i) return porIndice;
  return mensual.find((e) => e && Number(e.mes) === i) || null;
}

// ----------------------------------------------------------------------------
// Módulo 3 · Calendario por agotamiento (máximo intervalo, estrategia RAW)
// ----------------------------------------------------------------------------
// A diferencia de la estrategia "frecuencia fija" (un intervalo constante
// repetido), aquí se simula el agotamiento del agua fácilmente aprovechable
// (RAW) día a día: se acumula la demanda neta diaria del MES VIGENTE en cada
// punto del recorrido (no un único "mes peak" fijo para todo el periodo) y se
// registra un riego cada vez que el acumulado alcanza RAW, reiniciando el
// contador. El intervalo resultante entre riegos varía según la demanda de
// cada mes, en vez de ser un número único.
//
// params: { fechaInicio, fechaFin (Date|'YYYY-MM-DD'), demandaRows (=
//           computeDemanda().rows, 12 filas), raw (mm, dosis neta máxima),
//           ef(%), tasa(mm/h), areaHa, maxEventos (límite de seguridad) }
// retorna: [{ fecha:Date, dosisNeta, dosisBruta, volumen, tiempoH, caudal, mesIndex }, ...]
export function computeAgotamientoDates(params) {
  const {
    fechaInicio, fechaFin, demandaRows, raw = 0,
    ef: efRaw = 90, tasa = 0, areaHa = 0, maxEventos = 1000
  } = params || {};

  const start = fechaInicio instanceof Date ? new Date(fechaInicio) : new Date(`${fechaInicio}T00:00:00`);
  const end = fechaFin instanceof Date ? new Date(fechaFin) : new Date(`${fechaFin}T00:00:00`);
  if (!Array.isArray(demandaRows) || demandaRows.length < 12 || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [];
  }

  const ef = Number(efRaw) || 1;
  const eventos = [];
  let acumulado = 0;
  const cursor = new Date(start);

  while (cursor <= end && eventos.length < maxEventos) {
    const mesIndex = cursor.getMonth();
    const netDia = (Number(demandaRows[mesIndex]?.netMm) || 0) / DIAS[mesIndex];
    acumulado += netDia;

    if (raw > 0 && acumulado >= raw) {
      const dosisNeta = acumulado;
      const dosisBruta = dosisNeta / (ef / 100);
      const volumen = dosisBruta * areaHa * 10; // mm·ha × 10 = m³
      const tiempoH = tasa > 0 ? dosisBruta / tasa : 0;
      const caudal = tiempoH > 0 ? (volumen * 1000) / (tiempoH * 3600) : 0; // l/s
      eventos.push({ fecha: new Date(cursor), dosisNeta, dosisBruta, volumen, tiempoH, caudal, mesIndex });
      acumulado = 0;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return eventos;
}
